import { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { badgesAPI } from './services/api';
import SendStarDialog from './SendStarDialog';
import './ParticipantsDialog.css';

const normalizeStatus = (status) => {
  if (status === 1 || status === 'confirmed') return 'confirmed';
  if (status === -1 || status === 'declined') return 'declined';
  return 'pending';
};

function ParticipantsDialog({ event, title, currentUser, isPast, onClose, onUserClick }) {
  const { t } = useTranslation();
  const dialogTitle = title || `${event.name} - Participants`;
  const overlayRef = useRef(null);
  const [giveawayStars, setGiveawayStars] = useState(0);
  const [givenStars, setGivenStars] = useState({});
  const [starRecipient, setStarRecipient] = useState(null);

  useEffect(() => {
    if (!currentUser || !isPast) return;

    const load = async () => {
      const count = await badgesAPI.getGiveawayCount(currentUser.id).catch(() => 0);
      setGiveawayStars(count);

      const allRecipients = [
        { user: event.organizer },
        ...(event.participants || []).filter(p => p.user.id !== event.organizer.id),
      ];

      const checks = await Promise.all(
        allRecipients
          .filter(r => r.user.id !== currentUser.id)
          .map(async r => {
            const given = await badgesAPI.checkIfGiven(currentUser.id, r.user.id, event.id).catch(() => false);
            return [r.user.id, given];
          })
      );

      setGivenStars(Object.fromEntries(checks));
    };

    load();
  }, [currentUser?.id, event?.id]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      if (!overlayRef.current) return;
      overlayRef.current.style.top = `${vv.offsetTop}px`;
      overlayRef.current.style.left = `${vv.offsetLeft}px`;
      overlayRef.current.style.width = `${vv.width}px`;
      overlayRef.current.style.height = `${vv.height}px`;
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  const handleSubmitStar = async (message, isAnonymous, isPrivate) => {
    if (!starRecipient) return;
    try {
      await badgesAPI.giveBadge(starRecipient.user.id, event.id, message, isAnonymous, isPrivate);
      const count = await badgesAPI.getGiveawayCount(currentUser.id);
      setGiveawayStars(count);
      setGivenStars(prev => ({ ...prev, [starRecipient.user.id]: true }));
      setStarRecipient(null);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data || t('gatheringsSidebar.alerts.failedStar');
      alert(msg);
    }
  };

  const StarButton = ({ recipient }) => {
    const userId = recipient.user.id;
    if (!isPast || !currentUser || currentUser.id === userId) return null;
    if (givenStars[userId]) {
      return <span className="star-given-indicator" title={t('reactDialog.starGivenTitle')}>⭐✓</span>;
    }
    if (giveawayStars > 0) {
      return (
        <button
          className="send-star-btn"
          onClick={(e) => { e.stopPropagation(); setStarRecipient(recipient); }}
          title={t('reactDialog.sendStarTitle')}
        >
          ⭐
        </button>
      );
    }
    return null;
  };

  return createPortal(
    <>
      <div ref={overlayRef} className="participants-dialog-overlay" onClick={onClose}>
        <div className="participants-dialog" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
          <div className="dialog-header">
            <h4>{dialogTitle}</h4>
            <button className="close-dialog" onClick={onClose}>×</button>
          </div>
          {isPast && currentUser && giveawayStars === 0 && (
            <div className="no-stars-notice">{t('reactDialog.noStarsNotice')}</div>
          )}
          <div className="participants-list">
            <div
              className={`participant-item organizer${onUserClick ? ' clickable' : ''}`}
              onClick={onUserClick ? () => onUserClick(event.organizer.id) : undefined}
              style={onUserClick ? { cursor: 'pointer' } : undefined}
            >
              <div className="participant-info">
                <strong>{event.organizer.displayName}</strong>
                <span className="participant-username">@{event.organizer.username}</span>
              </div>
              <div className="participant-actions">
                <span className="participant-badge organizer-badge">{t('reactDialog.organizerBadge')}</span>
                <StarButton recipient={{ user: event.organizer }} />
              </div>
            </div>

            {event.participants
              .filter(p => p.user.id !== event.organizer.id)
              .map(participant => {
                const status = normalizeStatus(participant.status);
                return (
                  <div
                    key={participant.id}
                    className={`participant-item${onUserClick ? ' clickable' : ''}`}
                    onClick={onUserClick ? () => onUserClick(participant.user.id) : undefined}
                    style={onUserClick ? { cursor: 'pointer' } : undefined}
                  >
                    <div className="participant-info">
                      <strong>{participant.user.displayName}</strong>
                      <span className="participant-username">@{participant.user.username}</span>
                    </div>
                    <div className="participant-actions">
                      <span className={`participant-badge status-${status}`}>
                        {status === 'confirmed' && '✓ Confirmed'}
                        {status === 'pending' && '⏳ Pending'}
                        {status === 'declined' && '✗ Declined'}
                      </span>
                      <StarButton recipient={participant} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {starRecipient && (
        <SendStarDialog
          recipient={starRecipient}
          giveawayStars={giveawayStars}
          onClose={() => setStarRecipient(null)}
          onSubmit={handleSubmitStar}
        />
      )}
    </>,
    document.body
  );
}

export default ParticipantsDialog;
